import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser,
  faBuilding,
  faShieldHalved,
  faLeaf,
  faBell,
  faPalette,
  faLifeRing,
  faArrowRightFromBracket,
  faMapMarkerAlt,
  faHandHoldingHeart,
  faHandshake,
  faBoxOpen,
  faSitemap,
  faUsersGear,
  faUsersCog,
  faUserShield,
  faUserTie,
  faChartLine,
  faDatabase,
} from "@fortawesome/free-solid-svg-icons";

import { useDarkMode } from "../../contexts/ThemeContext";
import { useAuth } from "../../contexts/AuthContext";
import { useProfileData } from "../../hooks/useProfileData";
import LoadingSpinner from "../../components/ui/LoadingSpinner";

import GeneralSection from "../../components/Profile/GeneralSection";
import CompanySection from "../../components/Profile/CompanySection";
import SecuritySection from "../../components/Profile/SecuritySection";
import GHGGoalsSection from "../../components/Profile/GHGGoalsSection";
import NotificationsSection from "../../components/Profile/NotificationsSection";
import AppearanceSection from "../../components/Profile/AppearanceSection";
import SupportSection from "../../components/Profile/SupportSection";
import OrganizationsSection from "../../components/Profile/OrganizationsSection";
import UserManagementSection from "../../components/Profile/UserManagementSection";
import ManageAccountsSection from "../../components/Profile/ManageAccountsSection";
import PlatformActivitySection from "../../components/Profile/PlatformActivitySection";
import MaterialDataSection from "../../components/Profile/MaterialDataSection";
import PartnershipsSection from "../../components/Profile/PartnershipsSection";

import "../../style/Profile.css";
import "../../style/ProfileAdmin.css";

const NAV_ITEMS = {
  account: [
    { key: "general", icon: faUser, label: "General" },
    { key: "company", icon: faBuilding, label: "Company" },
    { key: "security", icon: faShieldHalved, label: "Security" },
    { key: "ghg-goals", icon: faLeaf, label: "GHG Goals" },
  ],
  preferences: [
    { key: "notifications", icon: faBell, label: "Notifications" },
    { key: "appearance", icon: faPalette, label: "Appearance" },
  ],
  companyAdmin: [
    { key: "manage-accounts", icon: faUsersCog, label: "Manage Accounts" },
    { key: "admin-partnerships", icon: faHandshake, label: "Partnerships" },
  ],
  siteAdmin: [
    { key: "platform-activity", icon: faChartLine, label: "Platform Activity" },
    { key: "organizations", icon: faSitemap, label: "Organizations" },
    { key: "user-management", icon: faUsersGear, label: "User Management" },
    { key: "material-data", icon: faDatabase, label: "Material Data" },
    { key: "admin-partnerships", icon: faHandshake, label: "Partnerships" },
  ],
  other: [{ key: "support", icon: faLifeRing, label: "Support" }],
};

function Profile() {
  const { t } = useTranslation();
  const { darkMode } = useDarkMode();
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { profile, loading, updateProfile, getFieldCooldown, cooldownDays } =
    useProfileData();

  // get role flags from user context
  const IS_MASTER_ADMIN = user?.is_master_admin === 1 || user?.is_master_admin === true;
  const IS_COMPANY_ADMIN = user?.is_company_admin === 1 || user?.is_company_admin === true;
  const CAN_DONATE = user?.can_donate === 1 || user?.can_donate === true;
  const CAN_RECEIVE = user?.can_receive === 1 || user?.can_receive === true;

  const [activeTab, setActiveTab] = useState("general");
  const [formData, setFormData] = useState({});
  const [isVisible, setIsVisible] = useState(false);
  const [picUrl, setPicUrl] = useState(null);

  useEffect(() => {
    if (profile?.profile_pic_url !== undefined) setPicUrl(profile.profile_pic_url);
  }, [profile?.profile_pic_url]);

  // handle tab from URL query param
  useEffect(() => {
    const tabParam = searchParams.get("tab");
    if (tabParam) {
      // validate that it's a valid tab
      const allTabs = [
        ...NAV_ITEMS.account,
        ...NAV_ITEMS.preferences,
        ...NAV_ITEMS.companyAdmin,
        ...NAV_ITEMS.siteAdmin,
        ...NAV_ITEMS.other,
      ].map((item) => item.key);

      if (allTabs.includes(tabParam)) {
        setActiveTab(tabParam);
      }
    }
  }, [searchParams]);

  useEffect(() => {
    if (profile) {
      setFormData({
        email: profile.email || "",
        phone_number: profile.phone_number || "",
        company_name: profile.company_name || "",
        address: profile.address || "",
        city: profile.city || "",
      });
    }
  }, [profile]);

  // compute isDirty by comparing current formData to original profile
  const isDirty = profile
    ? formData.email !== (profile.email || "") ||
      formData.phone_number !== (profile.phone_number || "") ||
      formData.company_name !== (profile.company_name || "") ||
      formData.address !== (profile.address || "") ||
      formData.city !== (profile.city || "")
    : false;

  useEffect(() => {
    if (!loading) {
      const timer = setTimeout(() => setIsVisible(true), 50);
      return () => clearTimeout(timer);
    }
  }, [loading]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    const result = await updateProfile(formData);
    return result;
  };

  const handleCancel = () => {
    if (profile) {
      setFormData({
        email: profile.email || "",
        phone_number: profile.phone_number || "",
        company_name: profile.company_name || "",
        address: profile.address || "",
        city: profile.city || "",
      });
    }
  };

  const handleSignOut = () => {
    logout();
    navigate("/login");
  };

  const profileTopRef = useRef(null);

  const handleTabChange = (tabKey) => {
    setActiveTab(tabKey);
    // scroll to top of the profile
    setTimeout(() => {
      profileTopRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 50);
  };

  const getInitials = (name) => {
    if (!name) return "?";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const renderContent = () => {
    const props = { darkMode, t, getFieldCooldown, cooldownDays };

    switch (activeTab) {
      case "general":
        return (
          <GeneralSection
            profile={profile}
            formData={formData}
            onChange={handleChange}
            onSave={handleSave}
            onCancel={handleCancel}
            isDirty={isDirty}
            onProfilePicChange={setPicUrl}
            {...props}
          />
        );
      case "company":
        return (
          <CompanySection
            profile={profile}
            formData={formData}
            onChange={handleChange}
            onSave={handleSave}
            onCancel={handleCancel}
            isDirty={isDirty}
            {...props}
          />
        );
      case "security":
        return <SecuritySection {...props} />;
      case "ghg-goals":
        return <GHGGoalsSection {...props} />;
      case "notifications":
        return <NotificationsSection {...props} />;
      case "appearance":
        return <AppearanceSection {...props} />;
      case "support":
        return <SupportSection {...props} />;
      case "organizations":
        return <OrganizationsSection {...props} />;
      case "platform-activity":
        return <PlatformActivitySection {...props} />;
      case "user-management":
        return <UserManagementSection {...props} />;
      case "manage-accounts":
        return <ManageAccountsSection {...props} />;
      case "material-data":
        return <MaterialDataSection {...props} />;
      case "admin-partnerships":
        return <PartnershipsSection {...props} />;
      default:
        return null;
    }
  };

  const renderNavItem = (item) => (
    <button
      key={item.key}
      className={`profile-nav-item ${activeTab === item.key ? "active" : ""}`}
      onClick={() => handleTabChange(item.key)}
    >
      <FontAwesomeIcon icon={item.icon} />
      <span>{t(item.label)}</span>
    </button>
  );

  if (loading) {
    return (
      <LoadingSpinner fullPage size={50} message={t("Loading profile...")} />
    );
  }

  return (
    <div
      className={`profile-container ${darkMode ? "dark" : "light"} ${isVisible ? "visible" : ""}`}
    >
      {/* User header card */}
      <div className="profile-header" ref={profileTopRef} style={{ scrollMarginTop: "20px" }}>
        <div className="profile-avatar">
          {picUrl
            ? <img src={picUrl} alt="profile" className="profile-avatar-img" onError={e => { e.target.style.display = "none"; }} style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "50%" }} />
            : getInitials(profile?.company_name || profile?.username)
          }

        </div>
        <div className="profile-header-info">
          <h1 className="profile-name">
            {profile?.company_name || profile?.username || profile?.email?.split('@')[0] || t("My Account")}
          </h1>
          <div className="profile-badges">
            {/* Role badge */}
            {IS_MASTER_ADMIN ? (
              <span className="profile-badge master-admin">
                <FontAwesomeIcon icon={faUserShield} />
                {t("PUNAR Master Admin")}
              </span>
            ) : IS_COMPANY_ADMIN ? (
              <span className="profile-badge company-admin">
                <FontAwesomeIcon icon={faUserTie} />
                {t("Company Admin")}
                {(CAN_DONATE || CAN_RECEIVE) && (
                  <span className="profile-badge-permissions">
                    {CAN_DONATE && <FontAwesomeIcon icon={faHandHoldingHeart} title={t("Donor")} />}
                    {CAN_RECEIVE && <FontAwesomeIcon icon={faBoxOpen} title={t("Receiver")} />}
                  </span>
                )}
              </span>
            ) : (
              <span className="profile-badge company-user">
                <FontAwesomeIcon icon={faUser} />
                {t("Company User")}
                {(CAN_DONATE || CAN_RECEIVE) && (
                  <span className="profile-badge-permissions">
                    {CAN_DONATE && <FontAwesomeIcon icon={faHandHoldingHeart} title={t("Donor")} />}
                    {CAN_RECEIVE && <FontAwesomeIcon icon={faBoxOpen} title={t("Receiver")} />}
                  </span>
                )}
              </span>
            )}
          </div>
          {profile?.city && (
            <div className="profile-location">
              <FontAwesomeIcon icon={faMapMarkerAlt} />
              {profile.city}
            </div>
          )}
        </div>
      </div>

      {/* main layout */}
      <div className="profile-main">
        {/* sidebar */}
        <aside className="profile-sidebar">
          <nav className="profile-nav-group">
            <div className="profile-nav-label">{t("Account")}</div>
            {NAV_ITEMS.account.map(renderNavItem)}
          </nav>

          <nav className="profile-nav-group">
            <div className="profile-nav-label">{t("Preferences")}</div>
            {NAV_ITEMS.preferences.map(renderNavItem)}
          </nav>

          {/* company admin section - only for company admins */}
          {IS_COMPANY_ADMIN && (
            <nav className="profile-nav-group admin-group company-admin">
              <div className="profile-nav-label">{t("Company Admin")}</div>
              {NAV_ITEMS.companyAdmin.map(renderNavItem)}
            </nav>
          )}

          {/* site admin section - only for master admins */}
          {IS_MASTER_ADMIN && (
            <nav className="profile-nav-group admin-group master-admin">
              <div className="profile-nav-label">{t("Master Admin")}</div>
              {NAV_ITEMS.siteAdmin.map(renderNavItem)}
            </nav>
          )}

          <nav className="profile-nav-group">
            {NAV_ITEMS.other.map(renderNavItem)}
            <button className="profile-nav-item danger" onClick={handleSignOut}>
              <FontAwesomeIcon icon={faArrowRightFromBracket} />
              <span>{t("Sign Out")}</span>
            </button>
          </nav>
        </aside>

        {/* content */}
        <main className="profile-content">{renderContent()}</main>
      </div>
    </div>
  );
}

export default Profile;
